// [claude-code 2026-04-23] S32-T4 — poll QuickScout (browser operator) status.
// [claude-code 2026-04-24] Hardened: a single 404 means the route is unwired
// (backend stub returns 404 only if the entire /api/consul-control mount is
// missing). When that happens we stop polling immediately so the dev console
// doesn't flood with 404s (was 231+ per session). Same rule for the
// `reason: "consul_control_not_wired"` payload from the new stub: treat as
// permanently inactive and stop polling.
import { useEffect, useRef, useState } from "react";
import { getRuntimeApiBase } from "../lib/runtime-api-base";

const BASE_POLL_MS = 10_000;
const MAX_POLL_MS = 60_000;
const STATUS_TIMEOUT_MS = 5_000;

export type ConsulControlStatus = "ok" | "degraded" | "error" | "unknown";

export interface ConsulControlHealth {
  active: boolean;
  status: ConsulControlStatus;
  detail: string;
}

export function useConsulControlHealth(): ConsulControlHealth {
  const [health, setHealth] = useState<ConsulControlHealth>({
    active: false,
    status: "unknown",
    detail: "Checking CAO handoff",
  });
  const cancelledRef = useRef(false);
  const inFlightRef = useRef(false);
  const errorCountRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    cancelledRef.current = false;
    const stopPolling = () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const scheduleNext = () => {
      if (cancelledRef.current) return;
      const backoff = Math.min(errorCountRef.current, 3);
      const delay = Math.min(BASE_POLL_MS * 2 ** backoff, MAX_POLL_MS);
      timerRef.current = window.setTimeout(tick, delay);
    };

    const tick = async (): Promise<void> => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      let shouldSchedule = true;
      try {
        const apiBase = getRuntimeApiBase().replace(/\/$/, "");
        const res = await fetch(`${apiBase}/api/consul-control/status`, {
          signal: AbortSignal.timeout(STATUS_TIMEOUT_MS),
        });
        if (res.status === 404) {
          // Feature not wired — stop polling for this session.
          if (!cancelledRef.current) {
            setHealth({
              active: false,
              status: "unknown",
              detail: "CAO chat handoff pending",
            });
          }
          shouldSchedule = false;
          stopPolling();
          return;
        }
        if (!res.ok) {
          errorCountRef.current += 1;
          if (!cancelledRef.current) {
            setHealth({
              active: false,
              status: "degraded",
              detail: `QuickScout status unavailable (${res.status})`,
            });
          }
          return;
        }
        const body = (await res.json()) as {
          active?: boolean;
          detail?: string;
          reason?: string;
          status?: string;
        };
        if (body?.reason === "consul_control_not_wired") {
          if (!cancelledRef.current) {
            setHealth({
              active: false,
              status: "unknown",
              detail: body.detail ?? "CAO chat handoff pending",
            });
          }
          shouldSchedule = false;
          stopPolling();
          return;
        }
        errorCountRef.current = 0;
        if (!cancelledRef.current) {
          const active = Boolean(body?.active);
          setHealth({
            active,
            status: active ? "ok" : "ok",
            detail:
              body.detail ??
              (active ? "CAO has control" : "CAO handoff standby"),
          });
        }
      } catch {
        errorCountRef.current += 1;
        if (!cancelledRef.current) {
          setHealth({
            active: false,
            status: "unknown",
            detail: "QuickScout status not reachable",
          });
        }
      } finally {
        inFlightRef.current = false;
        if (shouldSchedule) scheduleNext();
      }
    };

    void tick();
    return () => {
      cancelledRef.current = true;
      stopPolling();
    };
  }, []);

  return health;
}

export function useConsulControlStatus(): boolean {
  return useConsulControlHealth().active;
}
